import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'
import { observable, computed, action, autorun, makeObservable } from 'mobx'
import {
  type MATTING,
  type FILTERS,
  type MatColorKey,
  FRAME_COLORS,
  type TILE_SIZES,
  isCenterpiece,
} from '@mixtiles/web-backend-shared'
import uniq from 'lodash/uniq'
import StorageManager from 'services/StorageManager'
import { isFramedMaterial, MATERIAL_TYPE_KEY } from 'utils/materialTypeUtils'
import { getDefaultTileSize, SELECTED_SIZE_KEY } from 'hooks/tileStyling/utils'
import {
  BASE_STYLING,
  type BulkMaterial,
  FALLBACK_STYLE,
  MIXED_MATERIALS,
} from 'pages/PhotoStyler/TileDesignerConsts'
import { getTileStyleData } from 'services/tilesStyling'
import { type StylingFields } from './TilesStore.types'
import type { TilesStore } from './TilesStore'
import {
  getDefaultMaterialType,
  getFallbackStyleForMaterials,
} from './bulk-customization-utils'
import { FrameColorSchema, MattingColorSchema } from './tiles/tileSchemas'

export class BulkCustomizationStore {
  private readonly tilesStore: TilesStore

  @observable accessor materialType: BulkMaterial

  @observable accessor tileSize: TILE_SIZES

  @observable accessor frameColor: FRAME_COLORS

  @observable accessor matting: MATTING

  @observable accessor filter: FILTERS

  @observable accessor mattingColor: MatColorKey

  @observable accessor storedLastFramedColor: FRAME_COLORS

  @observable accessor currentSelection: string | null = null

  constructor(tilesStore: TilesStore) {
    this.tilesStore = tilesStore

    this.materialType = getDefaultMaterialType()
    this.tileSize =
      (StorageManager.get(SELECTED_SIZE_KEY) as TILE_SIZES) ??
      getDefaultTileSize()

    const storedBaseStyling = StorageManager.get(
      BASE_STYLING
    ) as StylingFields | null
    const initialStyling =
      storedBaseStyling ??
      readOutsideReaction(() => this.deriveInitialStyling())
    this.frameColor = initialStyling.frameColor ?? FALLBACK_STYLE.frameColor
    this.matting = initialStyling.matting ?? FALLBACK_STYLE.matting
    this.filter = initialStyling.filter ?? FALLBACK_STYLE.filter
    this.mattingColor = MattingColorSchema.parse(
      initialStyling.mattingColor ?? FALLBACK_STYLE.mattingColor
    )
    this.storedLastFramedColor = FALLBACK_STYLE.frameColor as FRAME_COLORS

    this.setupPersistence()
    this.setupReactions()
    makeObservable(this)
  }

  @computed
  get baseStyling(): StylingFields {
    return {
      frameColor: this.frameColor,
      matting: this.matting,
      filter: this.filter,
      mattingColor: this.mattingColor,
      lastFramedColor: this.lastFramedColor,
    }
  }

  @computed
  get bulkCustomization() {
    return {
      materialType: this.materialType,
      matting: this.matting,
      filter: this.filter,
      frameColor: this.frameColor,
      mattingColor: this.mattingColor,
      tileSize: this.tileSize,
    }
  }

  @computed
  get lastFramedColor(): FRAME_COLORS {
    const derivedColor = this.tilesStore.tiles
      .slice()
      .reverse()
      .map((tile) => {
        const { lastFramedColor, frameColor } = getTileStyleData(tile)
        if (frameColor !== FRAME_COLORS.NONE) return frameColor
        return lastFramedColor
      })
      .find((color) => !!color)

    const parsedLastFramedColor = FrameColorSchema.safeParse(derivedColor)

    return parsedLastFramedColor.success
      ? parsedLastFramedColor.data
      : this.storedLastFramedColor
  }

  isMixedStyling(property: keyof StylingFields): boolean {
    const nonCenterpiece = this.tilesStore.tiles.filter(
      (t) => !isCenterpiece(t)
    )
    return uniq(nonCenterpiece.map((t) => t[property])).length > 1
  }

  @action
  setMaterialType(bulkMaterial: BulkMaterial) {
    this.materialType = bulkMaterial
    if (bulkMaterial !== MIXED_MATERIALS && !isFramedMaterial(bulkMaterial)) {
      this.frameColor = FRAME_COLORS.NONE
    }
  }

  @action
  setTileSize(size: TILE_SIZES) {
    this.tileSize = size
  }

  @action
  setFrameColor(color: FRAME_COLORS) {
    this.frameColor = color
  }

  @action
  setMatting(matting: MATTING) {
    this.matting = matting
  }

  @action
  setFilter(filter: FILTERS) {
    this.filter = filter
  }

  @action
  setMattingColor(color: MatColorKey) {
    this.mattingColor = color
  }

  @action
  setCurrentSelection(sel: string | null) {
    this.currentSelection = sel
  }

  @action
  resetToFallbackStyle() {
    const frameColor = this.frameColor || FALLBACK_STYLE.frameColor
    this.frameColor = frameColor as FRAME_COLORS
    this.matting = FALLBACK_STYLE.matting as MATTING
    this.filter = FALLBACK_STYLE.filter as FILTERS
    this.mattingColor = FALLBACK_STYLE.mattingColor
  }

  private getBulkMaterialFromTiles(): BulkMaterial {
    const nonCenterpiece = this.tilesStore.nonCenterpieceTiles
    const materialTypes = uniq(nonCenterpiece.map((tile) => tile.materialType))
    return materialTypes.length > 1
      ? MIXED_MATERIALS
      : materialTypes[0] ?? getDefaultMaterialType()
  }

  private deriveInitialStyling(): StylingFields {
    const nonCenterpiece = this.tilesStore.nonCenterpieceTiles
    // With no tiles to derive from, the default material decides: a non-framed one must not
    // open on a frame color.
    const fallback = getFallbackStyleForMaterials(
      nonCenterpiece.length > 0
        ? nonCenterpiece.map((t) => t.materialType)
        : [this.materialType]
    )

    const mats = uniq(nonCenterpiece.map((t) => t.matting).filter(Boolean))
    const filters = uniq(nonCenterpiece.map((t) => t.filter).filter(Boolean))
    const frameColors = uniq(
      nonCenterpiece.map((t) => t.frameColor).filter(Boolean)
    )
    const matColors = uniq(
      nonCenterpiece.map((t) => t.mattingColor).filter(Boolean)
    )

    return {
      matting: (mats.length === 1 ? mats[0] : fallback.matting) as MATTING,
      filter: (filters.length === 1 ? filters[0] : fallback.filter) as FILTERS,
      frameColor: (frameColors.length === 1
        ? frameColors[0]
        : fallback.frameColor) as FRAME_COLORS,
      mattingColor:
        matColors.length === 1 ? matColors[0] : fallback.mattingColor,
    }
  }

  private setupReactions() {
    autorun(
      () => {
        const bulkMaterial = this.getBulkMaterialFromTiles()

        if (bulkMaterial !== this.materialType) {
          this.setMaterialType(bulkMaterial)
        }
      },
      { name: 'BulkCustomizationStore:syncBulkMaterial' }
    )
    autorun(
      () => {
        const nonCenterpieceTiles = this.tilesStore.nonCenterpieceTiles
        const tilesWithoutStyle = nonCenterpieceTiles.filter(
          (t) => t.frameColor === undefined && t.matting === undefined
        )
        for (const tile of tilesWithoutStyle) {
          this.tilesStore.applyTileStyling(tile.id, this.baseStyling)
        }
      },
      { name: 'BulkCustomizationStore:applyBaseStylingToNewTiles' }
    )
  }

  private setupPersistence() {
    autorun(
      () => {
        StorageManager.set(MATERIAL_TYPE_KEY, this.materialType)
      },
      { name: 'BulkCustomizationStore:persistMaterial', delay: 300 }
    )

    autorun(
      () => {
        StorageManager.set(BASE_STYLING, this.baseStyling)
      },
      { name: 'BulkCustomizationStore:persistBaseStyling', delay: 300 }
    )

    autorun(
      () => {
        StorageManager.set(SELECTED_SIZE_KEY, this.tileSize)
      },
      { name: 'BulkCustomizationStore:persistTileSize', delay: 300 }
    )
  }
}
